import { toast } from 'sonner';
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@apollo/client/react';

import { AgreementGenerateDocument } from 'src/graphql/actions/agreement/agreementGenerate.mutation';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { resetEscrowOnboardingStep } from 'src/utils/escrowOnboardingLocalStorage';

type Props = {
  triggerText: React.ReactNode;
};

const AgreementGenerateDialog = ({ triggerText }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const navigate = useNavigate();
  const {
    softwareEscrow: { id, agreement },
    refetch,
  } = useSoftwareEscrow();
  const [generateAgreement, { loading }] = useMutation(AgreementGenerateDocument, {
    onCompleted: refetch,
  });

  const onGenerate = async () => {
    try {
      const { data } = await generateAgreement({
        variables: {
          escrowId: id,
        },
      });

      if (data?.agreementGenerate?.success) setIsDialogOpen(true);
      else {
        throw new ClientError(data?.agreementGenerate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const onConfirmationDialogChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      resetEscrowOnboardingStep(id);
      navigate(`/software-escrows/${id}`);
    }
  };

  return (
    <Fragment>
      <Button isLoading={loading} disabled={!agreement?.policy.generate || loading} onClick={onGenerate}>
        {triggerText}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={onConfirmationDialogChange}>
        <DialogContent className="md:max-w-[440px]">
          <DialogHeader className="text-center">
            <DialogTitle>Agreement generation in progress!</DialogTitle>
            <DialogDescription>
              Thank you for submitting the information for your escrow. We will prepare the agreement, and let you know
              when it is ready for your review.
            </DialogDescription>
          </DialogHeader>
          <DialogClose asChild>
            <Button className="w-full">Go to escrow</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default AgreementGenerateDialog;
